import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

// Get Journal Entries
export const getJournalEntries = createAsyncThunk(
  "JournalEntry/getJournalEntries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);
      if (params.status) queryParams.append("status", params.status);
      if (params.start_date)
        queryParams.append("start_date", params.start_date);
      if (params.end_date) queryParams.append("end_date", params.end_date);
      if (params.account_id)
        queryParams.append("account_id", params.account_id);
      if (params.cost_center_id)
        queryParams.append("cost_center_id", params.cost_center_id);
      if (params.min_amount)
        queryParams.append("min_amount", params.min_amount);
      if (params.max_amount)
        queryParams.append("max_amount", params.max_amount);

      const response = await axios.get(
        `${BASE_URL}/journal-entries?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get journal entries!";
      return rejectWithValue(message);
    }
  },
);

// Get Journal Entry Details
export const getJournalEntryDetails = createAsyncThunk(
  "JournalEntry/getJournalEntryDetails",
  async (entryId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.get(
        `${BASE_URL}/journal-entries/${entryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get journal entry details!";
      return rejectWithValue(message);
    }
  },
);

// Add Journal Entry
export const addJournalEntry = createAsyncThunk(
  "JournalEntry/addJournalEntry",
  async (entryData, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }

      // Check if entryData is FormData (for file uploads) or regular object
      const isFormData = entryData instanceof FormData;

      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      // Don't set Content-Type for FormData, let browser set it with boundary
      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }

      const response = await axios.post(
        `${BASE_URL}/journal-entries`,
        entryData,
        {
          headers: headers,
        },
      );
      return response.data;
    } catch (error) {
      let message = "Failed to add journal entry!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// Update Journal Entry
export const updateJournalEntry = createAsyncThunk(
  "JournalEntry/updateJournalEntry",
  async ({ entryId, entryData }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const isFormData = entryData instanceof FormData;
      const headers = {
        Authorization: `Bearer ${token}`,
        "Accept-Language": currentLang,
      };

      if (!isFormData) {
        headers["Content-Type"] = "application/json";
      }

      const response = await axios.put(
        `${BASE_URL}/journal-entries/${entryId}`,
        entryData,
        {
          headers,
        },
      );
      return response.data;
    } catch (error) {
      let message = "Failed to update journal entry!";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.message) {
          message = errorData.message;
        } else if (typeof errorData === "object") {
          message = JSON.stringify(errorData);
        }
      }
      return rejectWithValue(message);
    }
  },
);

// Accept Journal Entry (Draft Only)
export const acceptJournalEntry = createAsyncThunk(
  "JournalEntry/acceptJournalEntry",
  async (entryId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(
        `${BASE_URL}/journal-entries/${entryId}/accept`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to accept journal entry!";
      return rejectWithValue(message);
    }
  },
);

// Post Journal Entry (Accepted Only)
export const postJournalEntry = createAsyncThunk(
  "JournalEntry/postJournalEntry",
  async (entryId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(
        `${BASE_URL}/journal-entries/${entryId}/post`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to post journal entry!";
      return rejectWithValue(message);
    }
  },
);

// Delete Journal Entry (Draft Only)
export const deleteJournalEntry = createAsyncThunk(
  "JournalEntry/deleteJournalEntry",
  async (entryId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.delete(
        `${BASE_URL}/journal-entries/${entryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete journal entry!";
      return rejectWithValue(message);
    }
  },
);

// Reverse Journal Entry (Posted Only)
export const reverseJournalEntry = createAsyncThunk(
  "JournalEntry/reverseJournalEntry",
  async (entryId, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      if (!token) {
        throw new Error("No authentication token found");
      }
      const response = await axios.post(
        `${BASE_URL}/journal-entries/${entryId}/reverse`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to reverse journal entry!";
      return rejectWithValue(message);
    }
  },
);
